import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminMonetization, adminCheckoutPreview, adminPreviewVendorSession } from "../lib/api";

const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

function Row({ tier, price, buyer, gets, flag, on, links, extra, testid }) {
  return (
    <tr className="align-top border-b border-[#ccc]" data-testid={testid}>
      <td className="py-2 pr-3 font-bold whitespace-nowrap">{tier}</td>
      <td className="py-2 pr-3 whitespace-pre-line">{price}</td>
      <td className="py-2 pr-3">{buyer}</td>
      <td className="py-2 pr-3 text-[#444]">{gets}</td>
      <td className="py-2 pr-3 whitespace-nowrap">
        <span className={on ? "font-bold" : "text-[#b91c1c]"} data-testid={`${testid}-flag`}>{on ? "ON (public)" : "off (admin-only)"}</span>
        <div className="text-[#888] text-[11px]">switch: {flag}</div>
      </td>
      <td className="py-2">
        {links.map((l) => (
          <a key={l.href} href={l.href} target="_blank" rel="noreferrer" className="tv-link block" data-testid={`${testid}-link-${l.label.replace(/\W+/g, "-")}`}>{l.label}</a>
        ))}
        {extra}
      </td>
    </tr>
  );
}

export default function AdminMonetization({ onOpenVendors }) {
  const [d, setD] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => {
    adminMonetization().then(setD).catch(() => toast.error("could not load monetization overview"));
  }, []);
  if (!d) return <div className="text-[13px] text-[#888]">loading…</div>;
  const { flags, stripe, starter, sponsored, claiming, preview } = d;

  const openCheckoutPreview = async (lookupKey) => {
    setPreviewing(true);
    try {
      const { checkout_url } = await adminCheckoutPreview(lookupKey);
      // Navigate the current tab — never blocked by popup blockers.
      window.location.href = checkout_url;
    } catch {
      toast.error("could not open checkout preview");
      setPreviewing(false);
    }
  };

  const openVendorPreview = async () => {
    setPreviewing(true);
    try {
      // Sign the admin in as the example listing's owner, then open the
      // real (login-gated) vendor dashboard in the current tab.
      await adminPreviewVendorSession();
      window.location.href = "/vendor";
    } catch {
      toast.error("could not open vendor dashboard preview");
      setPreviewing(false);
    }
  };

  const stat = (label, value, testid) => (
    <div className="border border-black p-2 min-w-[150px]" data-testid={testid}>
      <div className="text-[11px] text-[#666]">{label}</div>
      <div className="text-[18px] font-bold">{value}</div>
    </div>
  );

  return (
    <div className="space-y-8 text-[12px]" data-testid="admin-monetization">
      <section>
        <h2 className="text-[15px] font-bold border-b border-black pb-1">what we charge</h2>
        <p className="text-[#444] mt-2">
          Three ways money comes in. Each one is built and deployed but only the public sees it when its switch is ON (
          <button type="button" className="tv-link underline" onClick={onOpenVendors} data-testid="monetization-open-vendors">+ vendors tab</button>
          ). As admin you always see everything ON — open the preview links to see exactly what a vendor sees.
        </p>
        <p className="text-[#666] mt-1 text-[11px]" data-testid="monetization-preview-note">
          Pages marked ▶ need a vendor login. The buttons sign you in as the example listing's owner (or open Stripe in test mode) so you can
          see the login-gated screens without a Google account. To exit the preview, use "log out" on the site.
        </p>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-left" data-testid="monetization-table">
            <thead><tr className="border-b border-black text-[11px] text-[#666]"><th className="pr-3">tier</th><th className="pr-3">price</th><th className="pr-3">who pays</th><th className="pr-3">what they get</th><th className="pr-3">public?</th><th>preview as admin</th></tr></thead>
            <tbody>
              <Row testid="tier-claim" tier="Free claim" price="$0" buyer="Anyone at the tool's company (domain email or meta-tag proof)"
                gets="✓ claimed mark on the listing · send corrections · embeddable rank badge · access to /vendor dashboard" flag="claiming" on={flags.claiming}
                links={[{ label: "claim flow (unclaimed tool)", href: preview.claim }, { label: "claimed tool page (the example)", href: preview.tool }]}
                extra={
                  <button
                    type="button"
                    onClick={openVendorPreview}
                    className="tv-link block text-left underline"
                    data-testid="tier-claim-vendor-preview"
                  >
                    ▶ open vendor dashboard (signed in as example vendor)
                  </button>
                } />
              <Row testid="tier-starter" tier="Starter"
                price={`${usd(starter.first_year_monthly_usd)}/mo or ${usd(starter.first_year_yearly_usd)}/yr first year\nthen ${usd(starter.monthly_usd)}/mo or ${usd(starter.yearly_usd)}/yr\n(Stripe coupon ${starter.coupon}, 50% off)`}
                buyer="Claimed vendor, Stripe subscription (card)"
                gets="Owner-edited page: custom CTA button replaces the affiliate link, positioning text, video, screenshots, integrations · all edits approved by you before going live" flag="starter" on={flags.starter}
                links={[
                  ...(preview.example ? [{ label: "example Starter listing (what they get)", href: preview.example }] : []),
                  { label: "/for-vendors pricing page", href: preview.for_vendors },
                ]}
                extra={
                  <button
                    type="button"
                    onClick={() => openCheckoutPreview("starter_monthly")}
                    disabled={previewing}
                    className="tv-link block text-left underline disabled:opacity-50"
                    data-testid="tier-starter-checkout-preview"
                  >
                    {previewing ? "opening…" : "▶ preview the signup + payment screen (Stripe test)"}
                  </button>
                } />
              <Row testid="tier-sponsored" tier="Sponsored slot"
                price={`per month, by page tier (${sponsored.min_months}-month minimum, 12 months billed as ${sponsored.annual_months_charged}):\n${["home", "category", "standard", "alternatives"].map((k) => `${usd(sponsored.tiers[k])}/mo ${sponsored.tier_labels[k]}`).join("\n")}\n${Object.keys(sponsored.overrides).length ? `${Object.keys(sponsored.overrides).length} page override(s)` : "no per-page overrides"}`}
                buyer="Claimed vendor (one Stripe payment up front for 3, 6 or 12 months) or assigned by you (sold offline / comp)"
                gets={`Labelled "sponsored" row above the ranked list on the homepage, a /best/* or an /alternatives/* page · max ${sponsored.max_per_placement} per page (2 on the homepage) · ranking never changes`} flag="sponsored" on={flags.sponsored}
                links={[{ label: "homepage", href: "/" }, { label: "hub page with slots", href: preview.best }, { label: "alternatives page", href: preview.alternatives }, { label: "buy-a-slot panel in /vendor", href: preview.vendor }]} />
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-[15px] font-bold border-b border-black pb-1">numbers so far</h2>
        <div className="flex flex-wrap gap-2 mt-3">
          {stat("paid through Stripe (all time)", usd(d.revenue_usd), "stat-revenue")}
          {stat("Starter subscribers", `${starter.active} · ${usd(starter.mrr_usd)}/mo`, "stat-starter")}
          {stat("sponsored slots active", `${sponsored.active} of ${sponsored.inventory} · ${usd(sponsored.booked_usd)} booked`, "stat-sponsored")}
          {stat("claimed listings", `${claiming.claimed}${claiming.pending ? ` (+${claiming.pending} pending)` : ""}`, "stat-claimed")}
        </div>
        <p className="mt-2" data-testid="monetization-max-revenue">
          <b>max sponsored revenue if every slot sold: {usd(sponsored.max_month_usd)}/month · {usd(sponsored.max_annual_usd)}/year</b>
          <span className="text-[#666]"> at monthly rates ({usd(sponsored.max_annual_prepaid_usd)}/yr if everyone prepays annually) — {sponsored.inventory} slots across {sponsored.sellable_pages} pages: {sponsored.by_tier.map((t) => `${t.pages} ${t.label}${t.pages === 1 ? "" : "s"} at ${usd(t.price_usd)}/mo`).join(" · ")}. Plus Starter at {usd(starter.first_year_monthly_usd)}–{usd(starter.monthly_usd)}/mo per claimed vendor.</span>
        </p>
      </section>

      <section data-testid="monetization-stripe">
        <h2 className="text-[15px] font-bold border-b border-black pb-1">stripe</h2>
        <p className="mt-2">
          mode: <b data-testid="stripe-mode">{stripe.mode}</b>{stripe.mode === "test" && " — card 4242 4242 4242 4242 works, nobody is charged"}
          {!stripe.configured && <span className="text-[#b91c1c]"> · no Stripe key configured</span>}
        </p>
        <p className="text-[#666] mt-1">To collect real money: claim the Stripe account from the platform's Payments tab, then the live keys replace the test ones. Prices and the {starter.coupon} coupon are recreated automatically.</p>
      </section>

      <section data-testid="monetization-payments">
        <h2 className="text-[15px] font-bold border-b border-black pb-1">recent payments ({d.recent_payments.length})</h2>
        {d.recent_payments.length === 0 ? <p className="text-[#888] mt-2">none yet.</p> : (
          <ul className="mt-2 space-y-1">
            {d.recent_payments.map((p) => (
              <li key={p.session_id} className="flex flex-wrap gap-x-3">
                <span className="text-[#666]">{String(p.updated_at || "").slice(0, 10)}</span>
                <span className="font-bold">{usd(p.amount)}</span>
                <span>{p.kind === "sponsored" ? "sponsored" : p.lookup_key}</span>
                <a href={`/tool/${p.slug}`} className="tv-link" target="_blank" rel="noreferrer">{p.slug}</a>
                <span className="text-[#666]">{p.user_id}</span>
                {p.needs_attention && <span className="text-[#b91c1c]">⚠ {p.needs_attention}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
